(() => {
  'use strict';

  // Area 3 diagnostic only. Shopify/GA4 owns standard commerce events.
  // This file deliberately fails closed: no storage or third-party emission
  // happens until Shopify reports affirmative analytics consent.
  if (window.__rnAiAttributionV2 === true) return;
  window.__rnAiAttributionV2 = true;

  const EVENT_NAME = 'ai_discovery_visit';
  const SENT_KEY = 'rn:ai-attribution:v2:sent';
  const GA4_MEASUREMENT_ID = 'G-LGHZHFLDZN';
  const MAX_PATH_LENGTH = 300;
  const MAX_PRIVACY_WAIT_MS = 10000;
  const STARTED_AT = Date.now();

  const PUBLIC_PATH_PATTERNS = Object.freeze([
    /^\/$/,
    /^\/products(?:\/|$)/,
    /^\/collections(?:\/|$)/,
    /^\/pages(?:\/|$)/,
    /^\/blogs(?:\/|$)/,
    /^\/blog(?:\/|$)/,
    /^\/search\/?$/,
    /^\/cart\/?$/,
  ]);

  const HOST_SOURCES = Object.freeze({
    'chatgpt.com': 'chatgpt',
    'chat.openai.com': 'chatgpt',
    'gemini.google.com': 'gemini',
    'bard.google.com': 'gemini',
    'perplexity.ai': 'perplexity',
    'copilot.microsoft.com': 'microsoft_copilot',
  });
  const SOURCE_ALIASES = Object.freeze({
    chatgpt: 'chatgpt',
    gemini: 'gemini',
    google_ai: 'google_ai',
    'google-ai': 'google_ai',
    perplexity: 'perplexity',
    copilot: 'microsoft_copilot',
    microsoft_copilot: 'microsoft_copilot',
    'microsoft-copilot': 'microsoft_copilot',
  });
  const SOURCE_PLATFORMS = Object.freeze({
    chatgpt: 'chatgpt',
    gemini: 'gemini',
    google_ai: 'google_ai',
    perplexity: 'perplexity',
    microsoft_copilot: 'microsoft_copilot',
  });
  const RN_UTM_SOURCES = new Set(['rn-ai', 'rn_ai']);
  const RN_UTM_MEDIUMS = Object.freeze({
    chatgpt: 'chatgpt',
    gemini: 'gemini',
    google_ai: 'google_ai',
    'google-ai': 'google_ai',
    'ai-overview': 'google_ai',
    'ai-mode': 'google_ai',
    perplexity: 'perplexity',
    copilot: 'microsoft_copilot',
    microsoft_copilot: 'microsoft_copilot',
    'microsoft-copilot': 'microsoft_copilot',
  });

  const isInternalTraffic = () => {
    if (window.__rnInternalTraffic === true) return true;
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('rn_internal') === '1'
        || params.has('rn_qa')
        || params.has('rn_test')
        || params.has('preview_theme_id')
        || window.Shopify?.designMode === true;
    } catch (_error) {
      return false;
    }
  };

  if (isInternalTraffic()) return;

  const readParams = () => {
    try {
      return new URLSearchParams(window.location.search || '');
    } catch (_error) {
      return null;
    }
  };

  const normaliseToken = (value) => String(value || '').trim().toLowerCase();

  const hostFromReferrer = () => {
    try {
      const referrer = document.referrer || '';
      if (!referrer) return '';
      const hostname = new URL(referrer).hostname.replace(/^www\./, '').toLowerCase();
      return Object.keys(HOST_SOURCES).find((base) =>
        hostname === base || hostname.endsWith(`.${base}`)
      ) || '';
    } catch (_error) {
      return '';
    }
  };

  const sourceFromExplicitParams = (params) => {
    if (!params) return '';

    // rn_ai_source is an explicit RN-owned diagnostic parameter. It is not
    // inferred from arbitrary query text and is reduced to a fixed enum.
    const explicitSource = SOURCE_ALIASES[normaliseToken(params.get('rn_ai_source'))];
    if (explicitSource) return explicitSource;

    // Generic UTMs are ignored. Only links that identify themselves as RN's
    // AI diagnostic convention and use a fixed medium are accepted.
    const utmSource = normaliseToken(params.get('utm_source'));
    if (!RN_UTM_SOURCES.has(utmSource)) return '';
    return RN_UTM_MEDIUMS[normaliseToken(params.get('utm_medium'))] || '';
  };

  const publicStorefrontPath = () => {
    try {
      const path = String(window.location.pathname || '/').split(/[?#]/, 1)[0].trim();
      if (!path.startsWith('/')) return null;
      const bounded = path.slice(0, MAX_PATH_LENGTH).toLowerCase();
      return PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(bounded)) ? bounded : null;
    } catch (_error) {
      return null;
    }
  };

  const safeLandingPath = () => publicStorefrontPath() || '/';

  const safePageLocation = () => {
    try {
      return `${window.location.origin}${safeLandingPath()}`;
    } catch (_error) {
      return safeLandingPath();
    }
  };

  const safePageReferrer = (referrerHost) => referrerHost ? `https://${referrerHost}/` : '';

  const detectPayload = () => {
    const landingPath = publicStorefrontPath();
    if (!landingPath) return null;
    const params = readParams();
    const referrerHost = hostFromReferrer();
    const explicitSource = sourceFromExplicitParams(params);
    const referrerSource = referrerHost ? HOST_SOURCES[referrerHost] : '';
    const source = explicitSource || referrerSource;
    if (!source) return null;

    return {
      ai_source: source,
      ai_source_type: explicitSource ? 'utm' : 'referrer',
      landing_path: landingPath,
      referrer_host: referrerHost,
      rn_source_family: 'ai',
      rn_source_platform: SOURCE_PLATFORMS[source],
      rn_source_surface: 'ai_answer',
    };
  };

  const privacyApi = () => window.Shopify?.customerPrivacy || null;

  const hasAffirmativeAnalyticsConsent = () => {
    const privacy = privacyApi();
    if (!privacy
      || typeof privacy.analyticsProcessingAllowed !== 'function'
      || typeof privacy.currentVisitorConsent !== 'function') return false;

    try {
      const consent = privacy.currentVisitorConsent();
      return privacy.analyticsProcessingAllowed() === true
        && (consent?.analytics === 'yes' || consent?.analytics === true);
    } catch (_error) {
      return false;
    }
  };

  const sessionStorageIfAvailable = () => {
    try {
      const storage = window.sessionStorage;
      const probe = '__rn_ai_attribution_probe__';
      storage.setItem(probe, '1');
      storage.removeItem(probe);
      return storage;
    } catch (_error) {
      return null;
    }
  };

  const hasSentInThisSession = () => {
    if (window.__rnAiAttributionSentV2 === true) return true;
    const storage = sessionStorageIfAvailable();
    if (!storage) return false;
    try {
      return storage.getItem(SENT_KEY) === '1';
    } catch (_error) {
      return false;
    }
  };

  const sendOnce = (payload) => {
    if (!payload || !publicStorefrontPath() || isInternalTraffic() || !hasAffirmativeAnalyticsConsent()) return false;
    if (hasSentInThisSession()) {
      window.__rnAiAttributionSentV2 = true;
      return true;
    }
    if (typeof window.gtag !== 'function') return false;

    try {
      // Do not push to dataLayer or Shopify.analytics.publish: either would
      // create a second custom-event path beside the single GA4 owner.
      window.gtag('event', EVENT_NAME, {
        send_to: GA4_MEASUREMENT_ID,
        page_location: safePageLocation(),
        page_referrer: safePageReferrer(payload.referrer_host),
        ...payload,
      });
      window.__rnAiAttributionSentV2 = true;
      const storage = sessionStorageIfAvailable();
      if (storage) storage.setItem(SENT_KEY, '1');
      return true;
    } catch (_error) {
      return false;
    }
  };

  const payload = detectPayload();
  if (!payload) return;

  let loadRequested = false;
  let retryTimer = null;

  const attempt = () => {
    retryTimer = null;
    if (sendOnce(payload)) return;
    if (Date.now() - STARTED_AT >= MAX_PRIVACY_WAIT_MS) return;

    const privacy = privacyApi();
    if (!privacy && !loadRequested && typeof window.Shopify?.loadFeatures === 'function') {
      loadRequested = true;
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          () => attempt(),
        );
      } catch (_error) {}
    }
    if (!retryTimer) retryTimer = window.setTimeout(attempt, 250);
  };

  // The modern Shopify API emits this when the consent decision changes. The
  // current state is checked again rather than trusting arbitrary event data.
  document.addEventListener('visitorConsentCollected', attempt);
  document.addEventListener('trackingConsentAccepted', attempt);
  attempt();
})();
